// WP-1.9 (M10 TP-1..6, FE-4, S-16/17): third-party funds queries/mutations.
import {
  holdingForOthersResponseSchema,
  tpfCaseListResponseSchema,
  tpfCaseResponseSchema,
  type TpfCaseCreateRequest,
  type TpfCaseResponse,
  type TpfConvertRequest,
  type TpfMovementRequest,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';

export const TPF_KEY = ['tpf'] as const;

export function useTpfCases(filters: { status?: string } = {}): UseQueryResult<TpfCaseResponse[]> {
  const params = new URLSearchParams();
  if (filters.status !== undefined) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery({
    queryKey: [...TPF_KEY, filters],
    queryFn: async () => {
      const body = tpfCaseListResponseSchema.parse(
        await api.get(`/api/v1/tpf${qs === '' ? '' : `?${qs}`}`),
      );
      return body.cases;
    },
  });
}

export function useTpfCase(id: string): UseQueryResult<TpfCaseResponse> {
  return useQuery({
    queryKey: [...TPF_KEY, id],
    queryFn: async () => tpfCaseResponseSchema.parse(await api.get(`/api/v1/tpf/${id}`)),
  });
}

export function useHoldingForOthers(currency: string): UseQueryResult<number> {
  return useQuery({
    queryKey: [...TPF_KEY, 'holding', currency],
    queryFn: async () => {
      const body = holdingForOthersResponseSchema.parse(
        await api.get(`/api/v1/tpf/holding?currency=${currency}`),
      );
      return body.totalMinor;
    },
  });
}

function useInvalidateTpf(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TPF_KEY }),
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }), // receive/return move real cash
    ]);
  };
}

export function useCreateTpfCase(): UseMutationResult<
  TpfCaseResponse,
  Error,
  TpfCaseCreateRequest
> {
  const invalidate = useInvalidateTpf();
  return useMutation({
    mutationFn: async (input) => tpfCaseResponseSchema.parse(await api.post('/api/v1/tpf', input)),
    onSuccess: invalidate,
  });
}

export function useReceiveTpf(
  id: string,
): UseMutationResult<TpfCaseResponse, Error, TpfMovementRequest> {
  const invalidate = useInvalidateTpf();
  return useMutation({
    mutationFn: async (input) =>
      tpfCaseResponseSchema.parse(await api.post(`/api/v1/tpf/${id}/receive`, input)),
    onSuccess: invalidate,
  });
}

export function useReturnTpf(
  id: string,
): UseMutationResult<TpfCaseResponse, Error, TpfMovementRequest> {
  const invalidate = useInvalidateTpf();
  return useMutation({
    mutationFn: async (input) =>
      tpfCaseResponseSchema.parse(await api.post(`/api/v1/tpf/${id}/return`, input)),
    onSuccess: invalidate,
  });
}

export function useConvertTpf(
  id: string,
): UseMutationResult<TpfCaseResponse, Error, TpfConvertRequest> {
  const invalidate = useInvalidateTpf();
  return useMutation({
    mutationFn: async (input) =>
      tpfCaseResponseSchema.parse(await api.post(`/api/v1/tpf/${id}/convert`, input)),
    onSuccess: invalidate,
  });
}

export function useCloseTpfCase(id: string): UseMutationResult<TpfCaseResponse, Error, void> {
  const invalidate = useInvalidateTpf();
  return useMutation({
    mutationFn: async () => tpfCaseResponseSchema.parse(await api.post(`/api/v1/tpf/${id}/close`)),
    onSuccess: invalidate,
  });
}
